<template>
  <modal-wrap :name="modalName" :header-class="'user-header'" body-class="user-modal" :hide-footer="true" size="xl">
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">{{ modalTitle }}</h4>
      </div>
    </template>
    <template #default>
      <VeeForm @submit="handleSubmit">
        <div class="mt-2">
          <div class="row">
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :rules="{ required: true, min: 3 }"
                  :model-value="selectedUser.email"
                  name="email"
                >
                  <label for="email">Email *</label>
                  <input
                    v-bind="field"
                    v-model="selectedUser.email"
                    type="text"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field v-slot="{ field, errors, meta }" v-model="selectedUser.role" name="role" :rules="{ required: true }">
                  <label for="role">Role *</label>
                  <select v-bind="field" class="form-control" :class="getValidationState(meta)">
                    <option v-for="option in getRolesFor(CUSTOMER)" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="selectedUser.firstName"
                  name="firstName"
                  :rules="{ required: true, min: 3 }"
                >
                  <label for="company">First Name *</label>
                  <input
                    v-bind="field"
                    v-model="selectedUser.firstName"
                    type="text"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field v-slot="{ field, errors, meta }" :model-value="selectedUser.lastName" name="lastName">
                  <label for="lastName">Last Name</label>
                  <input
                    v-bind="field"
                    v-model="selectedUser.lastName"
                    type="text"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="password"
                  :rules="user ? { userPassword: true } : { password: true, required: true }"
                  name="password"
                >
                  <label for="password">Password{{ user ? '' : ' *' }}</label>
                  <input
                    v-bind="field"
                    v-model="password"
                    type="password"
                    autocomplete="off"
                    placeholder="Password"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="confirmedPassword"
                  :rules="user ? { confirmed: '@password', userPassword: true } : { confirmed: '@password', password: true, required: true }"
                  name="confirmedPassword"
                >
                  <label for="confirmedPassword">Confirm Password{{ user ? '' : ' *' }}</label>
                  <input
                    v-bind="field"
                    v-model="confirmedPassword"
                    type="password"
                    autocomplete="off"
                    placeholder="Confirm Password"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </div>

          <div class="sender-footer mt-3">
            <div v-if="!user" class="w-100 m-0">
              <b-button type="submit" variant="success" class="w-100"> Create </b-button>
            </div>
            <div v-else class="row w-100 m-0">
              <div class="col-8 p-0">
                <b-button type="submit" variant="success" class="w-100">Save</b-button>
              </div>
            </div>
          </div>
        </div>
      </VeeForm>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { Account } from '@/types/Models/Account'
import type { UsersActionModal } from '@/types/Models/User'
import { BButton } from 'bootstrap-vue-next'
import { Form as VeeForm, Field } from 'vee-validate'
import { defineComponent, computed, ref, type Ref, type ComputedRef, watch } from 'vue'
import { CUSTOMER_ROLE_COMPANY_ADMIN } from '@/const/AccountRoles'
import { CUSTOMER } from '@/const/AccountTypes'
import { USERS_CREATE_EDIT } from '@/const/ModalTypes'
import { getRolesFor, none } from '@/services/Helpers'
import { getValidationState } from '@/services/Validation'
import { useAccountStore } from '@/store/account'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'UserCreateEditModal',
  components: {
    BButton,
    ModalWrap,
    VeeForm,
    Field,
  },
  setup() {
    const accountStore = useAccountStore()
    const modalStore = useVueNextModalStore()

    const modalName: string = USERS_CREATE_EDIT
    const modalData: ComputedRef<UsersActionModal> = computed(() => modalStore.modalPropsByName(modalName))
    const selectedUser: Ref<Account> = ref({
      id: '',
      email: '',
      firstName: '',
      lastName: '',
      type: 'customer',
      role: CUSTOMER_ROLE_COMPANY_ADMIN,
      companyId: modalData.value?.companyId,
      isScannerOnlyEnabled: false,
      isInventoryAvailable: false,
    })
    const user: ComputedRef<Account | null> = computed(() => modalData.value?.user || null)
    const modalTitle: ComputedRef<string> = computed(() => (user.value ? 'Edit User' : 'Create User'))
    const password: Ref<string> = ref('')
    const confirmedPassword: Ref<string> = ref('')

    const handleSubmit = () => (user.value ? updateUser() : createUser())

    const closeUserModal = () => {
      if (modalData.value.companyId) {
        accountStore.fetchCompanyUsers(modalData.value.companyId).catch(none)
      }
      modalStore.closeModal(modalName)
    }

    const createUser = () => {
      accountStore
        .store({
          companyId: modalData.value.companyId,
          data: {
            ...selectedUser.value,
            password: password.value,
          },
        })
        .then((created) => (created === false ? none() : closeUserModal()))
    }

    const updateUser = () => {
      let data = { ...selectedUser.value } as Account
      if (password.value) {
        data.password = password.value
      }
      if (modalData.value?.user?.id) {
        accountStore.update({ id: modalData.value.user.id, data }).then(closeUserModal).catch(none)
      }
    }

    watch(
      modalData,
      () => {
        selectedUser.value = {
          id: modalData.value?.user?.id || '',
          email: modalData.value?.user?.email || '',
          firstName: modalData.value?.user?.firstName || '',
          lastName: modalData.value?.user?.lastName || '',
          type: 'customer',
          role: modalData.value?.user?.role ?? CUSTOMER_ROLE_COMPANY_ADMIN,
          companyId: modalData.value.companyId,
          isScannerOnlyEnabled: modalData.value?.user?.isScannerOnlyEnabled || false,
          isInventoryAvailable: modalData.value?.user?.isInventoryAvailable ?? false,
        }
        password.value = ''
        confirmedPassword.value = ''
      },
      { deep: true },
    )

    return {
      user,
      modalName,
      modalData,
      selectedUser,
      modalTitle,
      password,
      confirmedPassword,
      CUSTOMER_ROLE_COMPANY_ADMIN,
      CUSTOMER,
      getRolesFor,
      handleSubmit,
      getValidationState,
    }
  },
})
</script>
